import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { X, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCompare } from '../lib/compareStore';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RFQ_STORAGE_KEY = 'rfq_parts_v7';

const BASE_ROWS = [
  ['Description', (i) => i.description], ['Condition', (i) => i.condition || 'New'], ['TAA', (i) => i.taa],
  ['On Hand', (i) => i.qty_on_hand], ['Incoming', (i) => i.qty_incoming],
];

const addToRfq = (item) => {
  const parts = JSON.parse(localStorage.getItem(RFQ_STORAGE_KEY) || '[]');
  if (parts.some((p) => p.name === item.name)) { toast.info(`${item.name} is already in your RFQ`); return; }
  parts.push({ name: item.name, description: item.description, condition: item.condition, taa: item.taa,
    qty_on_hand: item.qty_on_hand, requested_qty: 1, available_qty_at_selection: item.qty_on_hand, selected_at: new Date().toISOString() });
  localStorage.setItem(RFQ_STORAGE_KEY, JSON.stringify(parts));
  toast.success(`Added ${item.name} to RFQ`);
};

const specRows = (items) => {
  const labels = [];
  items.forEach((i) => (i.ai_specs?.specs || []).forEach((s) => { if (!labels.includes(s.label)) labels.push(s.label); }));
  return labels.map((l) => [l, (i) => (i.ai_specs?.specs || []).find((s) => s.label === l)?.value]);
};

export default function ComparePage() {
  const [params, setParams] = useSearchParams();
  const { list, remove } = useCompare();
  const [items, setItems] = useState([]);
  const slugs = params.get('slugs') || list.map((p) => p.slug).join(',');

  useEffect(() => {
    document.title = 'Compare Server Memory Modules | ServerMemoryRFQ';
    if (!slugs) { setItems([]); return; }
    axios.get(`${API_URL}/api/inventory/compare`, { params: { slugs } }).then((r) => setItems(r.data.items)).catch(() => {});
  }, [slugs]);

  const drop = (slug) => {
    remove(slug);
    const rest = items.filter((i) => i.slug !== slug);
    setItems(rest);
    setParams(rest.length ? { slugs: rest.map((i) => i.slug).join(',') } : {}, { replace: true });
  };
  const rows = [...BASE_ROWS, ...specRows(items)];

  return (
    <div className="min-h-screen" data-testid="compare-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-10">
        <h1 className="text-4xl sm:text-5xl font-['JetBrains_Mono'] font-bold text-cyan-400 mb-3">Compare Modules</h1>
        <p className="text-[#94A3B8] mb-8">Side-by-side technical specs for up to 4 server memory modules.</p>
        {items.length === 0 ? (
          <div className="text-center py-16 font-mono text-[#94A3B8]" data-testid="compare-empty">
            No modules selected. <Link to="/inventory" className="text-cyan-400">Pick some from inventory →</Link>
          </div>
        ) : (
          <div className="overflow-x-auto border border-[#1E293B] rounded-lg">
            <table className="w-full text-sm" data-testid="compare-table">
              <thead>
                <tr className="bg-[#11141D]">
                  <th className="sticky left-0 bg-[#11141D] w-40 p-4" />
                  {items.map((i) => (
                    <th key={i.slug} className="p-4 text-left align-top min-w-[220px]" data-testid={`compare-col-${i.slug}`}>
                      <div className="flex items-start justify-between gap-2">
                        <Link to={`/product/${i.slug}`} className="font-mono font-bold text-cyan-400 hover:text-white break-all">{i.name}</Link>
                        <button onClick={() => drop(i.slug)} className="text-[#64748B] hover:text-white" aria-label="Remove" data-testid={`compare-remove-${i.slug}`}><X size={14} /></button>
                      </div>
                      <button onClick={() => addToRfq(i)} className="mt-3 inline-flex items-center gap-1 text-xs font-mono font-bold bg-[#10B981] hover:bg-[#059669] text-white px-3 py-1.5 rounded transition-colors" data-testid={`compare-add-rfq-${i.slug}`}>
                        <Plus size={12} weight="bold" /> Add to RFQ
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, get]) => {
                  const vals = items.map((i) => String(get(i) ?? '—'));
                  const differs = new Set(vals).size > 1;
                  return (
                    <tr key={label} className="border-t border-[#1E293B]">
                      <td className="sticky left-0 bg-[#05050A] p-3 text-[11px] font-mono uppercase tracking-wider text-[#64748B]">{label}</td>
                      {vals.map((v, idx) => (
                        <td key={idx} className={`p-3 font-mono ${differs ? 'text-white' : 'text-[#94A3B8]'}`}>{v}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {items.some((i) => !i.ai_specs) && (
          <p className="text-[11px] font-mono text-[#64748B] mt-3">Some modules are still having their detailed specs generated.</p>
        )}
      </main>
      <Footer />
    </div>
  );
}
